import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { GanttChart, ArrowLeft, Mail, ShieldCheck, Loader2 } from 'lucide-react';

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function Register() {
  const [step, setStep] = useState('form'); // 'form' | 'pending' | 'otp'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, initiateGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get redirect URL from query params
  const rawRedirect = searchParams.get('redirect');
  // Validate redirect is a safe relative path (starts with / but not //)
  const redirectUrl = rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') 
    ? rawRedirect 
    : '/dashboard';

  const handleRequestSignup = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authAPI.requestSignup({ name, email, password });
      const data = response.data;
      
      if (data.status === 'approved') {
        // Admin account - auto approved
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Admin account created successfully!');
        // Use navigate instead of window.location for proper routing
        window.location.href = redirectUrl;
      } else {
        // Regular user - needs OTP
        setStep('pending');
        toast.success('Signup request sent! Please wait for the admin to share your OTP.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authAPI.verifySignup({ email, otp, password, name });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success('Account created successfully!');
      window.location.href = redirectUrl;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    setGoogleLoading(true);
    // Store redirect URL in sessionStorage for after OAuth callback
    if (redirectUrl !== '/dashboard') {
      sessionStorage.setItem('authRedirect', redirectUrl);
    }
    initiateGoogleLogin();
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-900 p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <GanttChart className="w-6 h-6 text-white" />
            </div>
            <span className="font-outfit font-bold text-2xl text-white">ProjectOS</span>
          </Link>
        </div>
        <div className="max-w-md">
          <h2 className="font-outfit text-4xl font-bold text-white mb-4">
            Join ProjectOS Today
          </h2>
          <p className="text-zinc-400 text-lg">
            Start managing your projects with professional tools. WBS, Gantt charts, 
            Kanban boards, and more - all in one platform.
          </p>
        </div>
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <ShieldCheck className="w-4 h-4" />
          <span>Admin-approved signups for secure access</span>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col">
        <div className="p-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GanttChart className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl">ProjectOS</span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {step === 'form' && (
              <>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
                  Create your account
                </h1>
                <p className="text-zinc-600 mb-8">
                  Sign up to get started. Admin approval required.
                </p>

                <form onSubmit={handleRequestSignup} className="space-y-6">
                  {/* Google Sign Up Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleSignUp}
                    disabled={googleLoading}
                    data-testid="google-signup-btn"
                    className="w-full h-12 rounded-xl border-zinc-300 hover:bg-zinc-50 font-medium transition-all flex items-center justify-center gap-3"
                  >
                    {googleLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <GoogleIcon />
                        Continue with Google
                      </>
                    )}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-zinc-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-zinc-50 px-2 text-zinc-500">Or register with email</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-700">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      data-testid="register-name-input"
                      className="h-12 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-700">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                      className="h-12 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-700">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="register-password-input"
                      className="h-12 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                    <p className="text-xs text-zinc-500">Must be at least 6 characters</p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="register-submit-btn"
                    className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl font-medium transition-all active:scale-[0.98]"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Request Access'}
                  </Button>
                </form>
              </>
            )}

            {step === 'pending' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-orange-600" />
                </div>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
                  Request Sent!
                </h1>
                <p className="text-zinc-600 mb-8">
                  Your signup request has been sent to the admin.<br />
                  You'll receive an OTP once approved.
                </p>
                
                <div className="bg-zinc-100 rounded-xl p-6 mb-8 text-left">
                  <p className="text-sm text-zinc-600 mb-2">Registered email:</p>
                  <p className="font-medium text-zinc-900">{email}</p>
                </div>

                <Button
                  onClick={() => setStep('otp')}
                  data-testid="enter-otp-btn"
                  className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl font-medium transition-all"
                >
                  I have the OTP
                </Button>
                
                <p className="mt-4 text-sm text-zinc-500">
                  Contact the admin if you haven't received your OTP.
                </p>
              </div>
            )}

            {step === 'otp' && (
              <>
                <button
                  onClick={() => setStep('pending')}
                  className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2 text-center">
                  Enter OTP
                </h1>
                <p className="text-zinc-600 mb-8 text-center">
                  Enter the 6-digit code shared by the admin.
                </p>

                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-zinc-700">OTP Code</Label>
                    <Input
                      id="otp"
                      type="text"
                      placeholder="000000"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      maxLength={6}
                      data-testid="otp-input"
                      className="h-14 rounded-xl border-zinc-200 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-center text-2xl tracking-widest font-mono"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    data-testid="verify-otp-btn"
                    className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl font-medium transition-all active:scale-[0.98]"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Create Account'}
                  </Button>
                </form>
              </>
            )}

            {step === 'form' && (
              <p className="mt-8 text-center text-zinc-600">
                Already have an account?{' '}
                <Link 
                  to={redirectUrl !== '/dashboard' ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'} 
                  className="text-primary hover:underline font-medium" 
                  data-testid="login-link"
                >
                  Sign in
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
